// src/pages/workflow/WorkflowResults.js
import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Alert
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RefreshIcon from '@mui/icons-material/Refresh';

/**
 * Component for displaying workflow results
 * @param {Object} props
 * @param {string} props.category - Workflow category
 * @param {string} props.sourcingQueries - Sourcing queries JSON string
 * @param {string|Object} props.results - Final results
 * @param {string} props.output - Output string
 * @param {Function} props.onReset - Callback to reset workflow
 */
function WorkflowResults({ category, sourcingQueries, results, output, onReset }) {
  const parseJSON = (data) => {
    if (!data) return null;
    if (typeof data === 'object') return data;
    try {
      return JSON.parse(data);
    } catch {
      return data;
    }
  };

  const parsedResults = parseJSON(results);
  const parsedQueries = sourcingQueries
    ? (() => {
        try {
          return JSON.parse(sourcingQueries);
        } catch {
          return sourcingQueries;
        }
      })()
    : null;

  return (
    <Box sx={{ width: '100%', maxWidth: '100%', px: 2, py: 3, boxSizing: 'border-box' }}>
      <Alert
        severity="success"
        icon={<CheckCircleIcon />}
        sx={{ mb: 3 }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
          Workflow Complete!
        </Typography>
        {category && (
          <Typography variant="body2">
            Category: <strong>{category}</strong>
          </Typography>
        )}
      </Alert>

      {parsedQueries && (
        <Card sx={{ boxShadow: 3, mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Sourcing Queries
            </Typography>
            <Box
              component="pre"
              sx={{
                p: 2,
                bgcolor: '#f5f5f5',
                borderRadius: 1,
                overflow: 'auto',
                maxHeight: 300
              }}
            >
              {JSON.stringify(parsedQueries, null, 2)}
            </Box>
          </CardContent>
        </Card>
      )}

      {parsedResults && (
        <Card sx={{ boxShadow: 3, mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Results
            </Typography>
            {typeof parsedResults === 'object' ? (
              <Box
                component="pre"
                sx={{
                  p: 2,
                  bgcolor: '#f5f5f5',
                  borderRadius: 1,
                  overflow: 'auto',
                  maxHeight: 400
                }}
              >
                {JSON.stringify(parsedResults, null, 2)}
              </Box>
            ) : (
              <Typography variant="body1">{parsedResults}</Typography>
            )}
          </CardContent>
        </Card>
      )}

      {output && !parsedResults && (
        <Card sx={{ boxShadow: 3, mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Output
            </Typography>
            <Box
              component="pre"
              sx={{
                p: 2,
                bgcolor: '#f5f5f5',
                borderRadius: 1,
                overflow: 'auto',
                maxHeight: 400,
                whiteSpace: 'pre-wrap'
              }}
            >
              {output}
            </Box>
          </CardContent>
        </Card>
      )}

      {onReset && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <Button
            variant="contained"
            onClick={onReset}
            startIcon={<RefreshIcon />}
            sx={{
              bgcolor: '#1BE885',
              color: '#000',
              fontWeight: 600,
              '&:hover': { bgcolor: '#20ec84' }
            }}
          >
            Start New Workflow
          </Button>
        </Box>
      )}
    </Box>
  );
}

export default WorkflowResults;
